import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CidadeReferenceDataService } from 'src/app/shared/services/cidade-reference-data.service';

@Component({
  selector: 'app-mesorregiao-select',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule],
  templateUrl: './mesorregiao-select.component.html',
  styleUrls: ['./mesorregiao-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MesorregiaoSelectComponent),
      multi: true,
    },
  ],
})
export class MesorregiaoSelectComponent implements OnInit, OnDestroy {
  items: string[] = [];
  loading = false;
  value: string | null = null;
  isDisabled = false;

  @Input() ariaLabel = 'Mesorregiao';
  @Input() inputId?: string;
  @Input() notFoundText = 'Nenhuma mesorregiao encontrada';
  @Input() loadingText = 'Carregando...';
  @Input() clearable = true;
  @Input() searchable = true;
  @Input() virtualScroll = true;

  private readonly destroy$ = new Subject<void>();
  private onChange: (value: string | null) => void = () => undefined;
  onTouched: () => void = () => undefined;

  constructor(
    private readonly cidadeReferenceDataService: CidadeReferenceDataService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.cidadeReferenceDataService
      .getMesorregioes()
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.items = items;
        this.loading = false;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: string | null): void {
    this.value = this.normalizeValue(value);
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cdr.markForCheck();
  }

  onValueChange(value: string | null): void {
    this.value = this.normalizeValue(value);
    this.onChange(this.value);
    this.cdr.markForCheck();
  }

  private normalizeValue(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  }
}
